import { zodResolver } from "@hookform/resolvers/zod";
import { Close } from "@mui/icons-material";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Stack,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import { useAdvisoryCaseQuery } from "AdvisoryManagement/Api/Queries/useAdvisoryCaseQuery";
import { usePartnersQuery } from "AdvisoryManagement/Api/Queries/usePartnersQuery";
import { useProductCategoriesQuery } from "AdvisoryManagement/Api/Queries/useProductCategoriesQuery";
import { useProductsQuery } from "AdvisoryManagement/Api/Queries/useProductsQuery";
import type { DummyExtendedContract } from "AdvisoryManagement/types";
import { useUpsertCommissionDistributionMutation } from "CommissionDistribution/Api/Mutations/useUpsertCommissionDistributionMutation";
import { useCommissionDistributionQuery } from "CommissionDistribution/Api/Queries/useCommissionDistributionQuery";
import { CommissionDistributionConfirmSection } from "CommissionDistribution/Components/CommissionDistributionConfirmSection";
import { CommissionDistributionContractMeta } from "CommissionDistribution/Components/CommissionDistributionContractMeta";
import { CommissionDistributionSuccessDialog } from "CommissionDistribution/Components/CommissionDistributionSuccessDialog";
import { CommissionDistributionTable } from "CommissionDistribution/Components/CommissionDistributionTable";
import {
	type ConsultantOption,
	type ContractMeta,
	ParticipantType,
} from "CommissionDistribution/Models/types";
import {
	CommissionDistributionResource,
	useCommissionDistributionResource,
} from "CommissionDistribution/Translations/CommissionDistributionResource";
import {
	type CommissionDistributionFormValues,
	useCommissionDistributionSchema,
} from "CommissionDistribution/Utils/commissionDistributionSchema";
import { useConsultantsQuery } from "Consultants/Api/Queries/useConsultantsQuery";
import useHandleServerError from "Forms/Hooks/useHandleServerError";
import type {
	CommissionDistributionResponse,
	ConsultantResponse,
	ParticipantType as ParticipantTypeApi,
} from "Infrastructure/Api/Api";

export type CommissionDistributionDialogProps = {
	contract: DummyExtendedContract;
	onClose: () => void;
};

const emptyFormValues: CommissionDistributionFormValues = {
	isConfirmed: false,
	participants: [],
};

const DUMMY_REALIZATOR: ConsultantOption = {
	consultantId: "dummy-realizator",
	fullName: "Petr Klaus",
};

export const CommissionDistributionDialog = ({
	contract,
	onClose,
}: CommissionDistributionDialogProps) => {
	const { t } = useCommissionDistributionResource();
	const R = CommissionDistributionResource;

	const contractId = contract.contractId;
	const contractMeta = useResolvedContractMeta(contract);
	const advisoryCaseQuery = useAdvisoryCaseQuery(contract.advisoryCaseId);
	const realizatorConsultantId = advisoryCaseQuery.data?.consultantId;

	const cdQuery = useCommissionDistributionQuery(contractId);
	const consultantsQuery = useConsultantsQuery();
	const upsert = useUpsertCommissionDistributionMutation();

	const [successOpen, setSuccessOpen] = useState(false);

	const schema = useCommissionDistributionSchema();
	const form = useForm({
		resolver: zodResolver(schema),
		defaultValues: emptyFormValues,
	});

	useHandleServerError({
		form,
		error: upsert.error,
		formKey: "isConfirmed",
		resource: R.Errors,
	});

	useEffect(() => {
		if (cdQuery.data) {
			form.reset(mapResponseToFormValues(cdQuery.data));
		} else if (cdQuery.data === null) {
			form.reset(emptyFormValues);
		}
	}, [cdQuery.data, form]);

	const consultants: ConsultantOption[] = useMemo(
		() =>
			(consultantsQuery.data ?? [])
				.filter((c): c is ConsultantResponse & { id: string } => !!c.id)
				.map((c) => ({
					consultantId: c.id,
					fullName: getConsultantFullName(c),
				})),
		[consultantsQuery.data],
	);

	const realizator = useMemo<ConsultantOption | null>(() => {
		const found = realizatorConsultantId
			? (consultants.find((c) => c.consultantId === realizatorConsultantId) ??
				null)
			: null;
		return found ?? DUMMY_REALIZATOR;
	}, [consultants, realizatorConsultantId]);

	const participantValues =
		useWatch({ control: form.control, name: "participants" }) ?? [];
	const nonRealizatorSum = participantValues.reduce(
		(acc, p) => acc + (Number(p.amount) || 0),
		0,
	);
	const isOverLimit = nonRealizatorSum > 100;

	const handleSave = form.handleSubmit((values) => {
		upsert.mutate(
			{
				contractId,
				request: {
					isConfirmed: values.isConfirmed,
					participants: values.participants.map((p) => ({
						commissionDistributionParticipantId:
							p.commissionDistributionParticipantId ?? null,
						participantType: p.participantType as ParticipantTypeApi,
						consultantId: p.consultantId,
						amount: p.amount,
					})),
				},
			},
			{ onSuccess: () => setSuccessOpen(true) },
		);
	});

	const summaryParticipants = useMemo(() => {
		if (!upsert.data) return [];
		const realizatorAmount =
			upsert.data.participants.find(
				(p) => p.participantType === ParticipantType.Realizator,
			)?.amount ?? 0;
		return [
			{
				consultant: realizator,
				participantType: ParticipantType.Realizator,
				amount: realizatorAmount,
			},
			...upsert.data.participants
				.filter((p) => p.participantType !== ParticipantType.Realizator)
				.map((p) => ({
					consultant:
						consultants.find((c) => c.consultantId === p.consultantId) ?? null,
					participantType: p.participantType,
					amount: p.amount,
				})),
		];
	}, [upsert.data, realizator, consultants]);

	const isBusy =
		upsert.isPending || cdQuery.isLoading || consultantsQuery.isLoading;

	return (
		<>
			<Dialog open onClose={onClose} fullWidth maxWidth="md">
				<DialogTitle>
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="flex-start"
					>
						<Box>
							{!!contractMeta && (
								<CommissionDistributionContractMeta meta={contractMeta} />
							)}
							<Typography variant="h6" fontWeight={800}>
								{t(R.Dialog.Title)}
							</Typography>
							<Typography variant="caption" color="text.secondary">
								{t(R.Dialog.Subtitle)}
							</Typography>
						</Box>
						<IconButton
							onClick={onClose}
							aria-label={t(R.Dialog.CloseAriaLabel)}
						>
							<Close />
						</IconButton>
					</Stack>
				</DialogTitle>
				<DialogContent dividers>
					<Stack spacing={2}>
						<CommissionDistributionTable
							control={form.control}
							consultants={consultants}
							realizator={realizator}
						/>
						<CommissionDistributionConfirmSection control={form.control} />
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose} disabled={isBusy}>
						{t(R.Dialog.CancelButton)}
					</Button>
					<Button
						onClick={handleSave}
						variant="contained"
						color="primary"
						disabled={isOverLimit || isBusy}
					>
						{t(R.Dialog.SaveButton)}
					</Button>
				</DialogActions>
			</Dialog>

			<CommissionDistributionSuccessDialog
				open={successOpen}
				isConfirmed={upsert.data?.isConfirmed ?? false}
				participants={summaryParticipants}
				onClose={onClose}
			/>
		</>
	);
};

const getConsultantFullName = (c: ConsultantResponse): string => {
	const firstName = c.person?.firstName?.trim() ?? "";
	const lastName = c.person?.lastName?.trim() ?? "";
	const personName = [firstName, lastName].filter(Boolean).join(" ");
	return personName || c.company?.name?.trim() || "";
};

const useResolvedContractMeta = (
	contract: DummyExtendedContract,
): ContractMeta => {
	const categories = useProductCategoriesQuery();
	const partners = usePartnersQuery(contract.categoryCode, {
		enabled: !!contract.categoryCode,
	});
	const products = useProductsQuery(
		contract.categoryCode,
		contract.institutionCode,
		{
			enabled: !!contract.categoryCode && !!contract.institutionCode,
		},
	);

	const category = categories.data?.find(
		(c) => c.code === contract.categoryCode,
	);
	const partner = partners.data?.find(
		(p) => p.code === contract.institutionCode,
	);
	const product = products.data?.find((p) => p.code === contract.productCode);

	const divisionSegment = category?.path?.split(".")[0];

	return {
		division: divisionSegment ?? "Investice",
		category: category?.name ?? "Podílové fondy",
		product: product?.name ?? "Conseq Invest Akciový",
		partner: partner?.name ?? "Conseq Investment Management",
	};
};

const mapResponseToFormValues = (
	data: CommissionDistributionResponse,
): CommissionDistributionFormValues => ({
	isConfirmed: data.isConfirmed,
	participants: data.participants
		.filter((p) => p.participantType !== ParticipantType.Realizator)
		.map((p) => ({
			commissionDistributionParticipantId:
				p.commissionDistributionParticipantId,
			participantType: p.participantType as Exclude<
				CommissionDistributionFormValues["participants"][number]["participantType"],
				"Realizator"
			>,
			consultantId: p.consultantId,
			amount: p.amount,
		})),
});
